"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CorreoEnviosResponseSchema = exports.CorreoEnvioSchema = exports.EstadoCorreoEnvioSchema = exports.ConexionCorreoResponseSchema = exports.EmisoresCorreoResponseSchema = exports.EmisorCorreoSchema = exports.CredencialCorreoSchema = exports.EstadoCredencialCorreoSchema = void 0;
const zod_1 = require("zod");
// ───────────── Emisores y salud de su credencial (R60) ─────────────
//
// La cuenta emisora es una cuenta de PERSONA, no de servicio: DTIC le fuerza
// cambio de clave cada 6 meses, y Google revoca el refresh_token de OAuth cuando
// cambia la clave de una cuenta cuyo consentimiento incluye scopes de Gmail. El
// sistema queda mudo sin avisar, y el mismo token lleva `drive.file`, así que
// también se corta la subida de respaldos.
//
// Este contrato expone el ESTADO de esa credencial —nunca el secreto— para que
// el panel pueda avisar antes de que muera.
/**
 * - `sin_configurar`: falta el client OAuth o el refresh token. Nunca se envió nada.
 * - `activa`: la última sonda contra Google funcionó y queda holgura de calendario.
 * - `por_vencer`: entra en la ventana de aviso (ver `diasRestantes`).
 * - `vencida`: la fecha ya pasó, o la última sonda falló. El sistema NO está enviando.
 * - `desconocida`: hay credencial pero nunca se sondeó ni se sabe cuándo se autorizó.
 */
exports.EstadoCredencialCorreoSchema = zod_1.z.enum([
    'sin_configurar',
    'activa',
    'por_vencer',
    'vencida',
    'desconocida',
]);
exports.CredencialCorreoSchema = zod_1.z.object({
    estado: exports.EstadoCredencialCorreoSchema,
    proveedor: zod_1.z.literal('google'),
    /** Client OAuth presente en `secrets/google/`. */
    tieneClienteOauth: zod_1.z.boolean(),
    /** Refresh token disponible para ESTA cuenta (su archivo, o la env legada). */
    tieneRefreshToken: zod_1.z.boolean(),
    /**
     * Client ID enmascarado. No es un secreto —el secreto es el client_secret—
     * pero se recorta igual: sirve para reconocer CUÁL client está en uso sin
     * publicarlo entero en una pantalla que alguien va a mostrar en una reunión.
     */
    clientIdMascara: zod_1.z.string().nullable(),
    cuentaAutorizada: zod_1.z.string().nullable(),
    autorizadoEn: zod_1.z.string().nullable(),
    /** Vencimiento: la fecha real si alguien la cargó, si no la estimada. */
    expiraEn: zod_1.z.string().nullable(),
    /** `true` cuando `expiraEn` salió de `autorizadoEn` + la ventana de la política. */
    expiraEsEstimado: zod_1.z.boolean(),
    /** Negativo = ya venció. `null` cuando no hay con qué calcularlo. */
    diasRestantes: zod_1.z.number().int().nullable(),
    verificadoEn: zod_1.z.string().nullable(),
    verificadoOk: zod_1.z.boolean().nullable(),
    verificadoError: zod_1.z.string().nullable(),
    avisadoEn: zod_1.z.string().nullable(),
});
exports.EmisorCorreoSchema = zod_1.z.object({
    id: zod_1.z.number().int().positive(),
    direccion: zod_1.z.string(),
    nombreRemitente: zod_1.z.string(),
    sistema: zod_1.z.string().nullable(),
    activo: zod_1.z.boolean(),
    /**
     * `true` para el emisor por defecto (`MAIL_FROM_ADDRESS`): el que usa una
     * plantilla que no declara emisor propio, y el que sostiene la subida de
     * respaldos a Drive. No es "el único con credencial" — desde R56 cada cuenta
     * autoriza la suya.
     */
    esRemitenteDelSistema: zod_1.z.boolean(),
    /** Estado de SU credencial. `null` solo si la base no pudo responder. */
    credencial: exports.CredencialCorreoSchema.nullable(),
    /** Envíos de las últimas 24 h, para ver de un vistazo si algo se está perdiendo. */
    envios24h: zod_1.z.object({
        enviados: zod_1.z.number().int().nonnegative(),
        fallidos: zod_1.z.number().int().nonnegative(),
        omitidos: zod_1.z.number().int().nonnegative(),
    }),
});
exports.EmisoresCorreoResponseSchema = zod_1.z.object({
    emisores: zod_1.z.array(exports.EmisorCorreoSchema),
    /**
     * Si hay client OAuth en `secrets/google/`. Sin él no se puede conectar
     * ninguna cuenta.
     *
     * La URL del consentimiento NO viaja acá: se pide al apretar "Conectar"
     * (`POST .../conectar`). Cada URL lleva un `state` de un solo uso, así que
     * emitir una por fila en cada carga del panel gastaría nonces que nadie va a
     * canjear.
     */
    puedeConectar: zod_1.z.boolean(),
    /** Días de vigencia que asume la política cuando no hay fecha real cargada. */
    vigenciaDias: zod_1.z.number().int().positive(),
});
/** Respuesta de `POST /admin/correos/emisores/conectar`. */
exports.ConexionCorreoResponseSchema = zod_1.z.object({
    /** A dónde mandar el navegador para el consentimiento de Google. */
    url: zod_1.z.string(),
    cuenta: zod_1.z.string(),
});
// ───────────── Bitácora de envíos (correo_envio) ─────────────
exports.EstadoCorreoEnvioSchema = zod_1.z.enum(['enviado', 'fallido', 'omitido']);
exports.CorreoEnvioSchema = zod_1.z.object({
    id: zod_1.z.string(),
    remitente: zod_1.z.string(),
    destinatario: zod_1.z.string(),
    plantillaClave: zod_1.z.string().nullable(),
    asunto: zod_1.z.string(),
    estado: exports.EstadoCorreoEnvioSchema,
    error: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string(),
});
exports.CorreoEnviosResponseSchema = zod_1.z.object({
    envios: zod_1.z.array(exports.CorreoEnvioSchema),
    total: zod_1.z.number().int().nonnegative(),
});
